import { computed } from "@vue/composition-api";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";

const MAX_SHOWED_FILES_IN_CATEGORY = 4;
const categoryFields: FragmentFields<VaultCategory> = [
  "name",
  "id",
  "system",
  "type",
  { files: ["id", "urlPart", "metadata"] },
];

export function useVaultCategory() {
  const { authUserStrict } = useAuthUser();

  const { enableScroll, items, data, isLastPage, getNextPage, getFirstPage, updateItem, removeItem } = usePager(
    page => {
      return providers.VaultsProvider.searchCategories(
        {
          data: {
            userId: authUserStrict.value.id,
            sort: "TYPE,ASC;NAME,ASC",
            page,
          },
        },
        categoryFields
      );
    }
  );

  const vaultCategoriesCount = computed(() => data.value?.totalRecords || 0);

  const updateCategory = (category: VaultCategory) => {
    updateItem(category, i => i.id === category.id);
  };

  const removeCategory = (categoryId: string) => {
    return removeItem(i => i.id === categoryId);
  };

  const updateCategoryPreviewFiles = (categoryId: string, file: VaultFile) => {
    const categoryIndex = items.value.findIndex(c => c.id === categoryId);
    if (categoryIndex > -1 && data.value) {
      if ((items.value[categoryIndex].files?.length as number) === MAX_SHOWED_FILES_IN_CATEGORY) {
        data.value.result[categoryIndex].files?.splice(0, 1, file);
      } else {
        data.value.result[categoryIndex].files?.unshift(file);
      }
    }
  };

  const getCategoryPreview = async (categoryIds: string[]) => {
    const categoryNames = items.value.filter(i => categoryIds.includes(i.id)).map(i => i.name);
    const responses = await Promise.all(
      categoryNames.map(categoryName =>
        providers.VaultsProvider.searchCategories(
          {
            data: {
              userId: authUserStrict.value.id,
              categoryName,
              size: 1,
            },
          },
          categoryFields
        )
      )
    );

    responses.forEach(response => response.result.length && updateCategory(response.result[0]));
  };

  const iterativelyFindCategory = async (categoryId: string) => {
    await getFirstPage();
    while (!items.value.some(i => i.id === categoryId) && !isLastPage.value) {
      await getNextPage();
    }
  };

  return {
    vaultCategories: items,
    vaultCategoriesCount,
    categoryScroll: enableScroll,
    getNextCategoryPage: getNextPage,
    getFirstCategoryPage: getFirstPage,
    updateCategory,
    removeCategory,
    updateCategoryPreviewFiles,
    iterativelyFindCategory,
    getCategoryPreview,
  };
}
